import { isObject } from 'lodash-es'
import Cookies from 'universal-cookie'

import { IS_PRODUCTION } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/constants'

import { LOG_CATEGORY, logToSentry } from './userUtils'
import { ONE_TRUST_COOKIE_BANNER_ROUTES } from '../../features/onboard/data/page-navigation/location-actions'

export const removeParentDomainCookies = () => {
  const cookies = new Cookies()
  const allCookies = cookies.getAll({ doNotParse: true })
  const parentDomain = '.lyrahealth.com'

  /**
   * Allow list for cookies that belong to the parent ".lyrahealth.com" domain
   * which we want to continue existing on our sub-domain (ex: ebay.lyrahealth.com).
   *
   * OptanonConsent: Cookie set by our OneTrust solution
   */
  const PARENT_DOMAIN_COOKIE_ALLOW_LIST = ['OptanonConsent', 'fpconsent']

  if (isObject(allCookies)) {
    Object.entries(allCookies).forEach(([cookieName]) => {
      if (!PARENT_DOMAIN_COOKIE_ALLOW_LIST.includes(cookieName)) {
        // The cookie will only be deleted if the domain and path matches.
        // Our sub-domain specific cookies will remain intact.
        cookies.remove(`${cookieName}`, { path: '/', domain: parentDomain })
      }
    })
  }
}

export const toggleOneTrustBanner = (pathname: string) => {
  const oneTrustWidget = window.document.getElementById('onetrust-consent-sdk')
  if (oneTrustWidget) {
    oneTrustWidget.hidden = !ONE_TRUST_COOKIE_BANNER_ROUTES.includes(pathname)
  }
}

export const initializeOneTrust = (detectedCountryIso: string, pathname: string) => {
  try {
    const head = window.document.getElementsByTagName('head')[0]
    // Before we intialize the script we first have to set the location info. This is
    // done because OneTrust geolocation detection endpoint is less reliable than ours. So
    // we force the location info by setting the country value in a script that the one trust script will use
    // instead of it's own IP detection.
    let oneTrustLocation = window.document.getElementById('onetrust-consent-location')
    if (!oneTrustLocation) {
      oneTrustLocation = window.document.createElement('script')
      const oneTrustLocationObject = {
        geolocationResponse: {
          // We don't currently gather state information so we use the XX default value. This is fine for GDPR countries
          // however if we ever need to show the banner in certain US states we will need to update this.
          stateCode: 'XX',
          countryCode: detectedCountryIso,
        },
      }
      oneTrustLocation.innerHTML = `var OneTrust = ${JSON.stringify(oneTrustLocationObject)}`
      oneTrustLocation.id = 'onetrust-consent-location'
      head.insertBefore(oneTrustLocation, head.firstChild)
    }
    oneTrustLocation = window.document.getElementById('onetrust-consent-location')
    const oneTrustWidget = window.document.getElementById('onetrust-consent-script')
    if (ONE_TRUST_COOKIE_BANNER_ROUTES.includes(pathname) && !oneTrustWidget && oneTrustLocation) {
      const scriptOneTrust = window.document.createElement('script')
      scriptOneTrust.src = 'https://cdn.cookielaw.org/scripttemplates/otSDKStub.js'
      scriptOneTrust.type = 'text/javascript'
      scriptOneTrust.setAttribute('data-domain-script', '01964070-cb1f-7c99-aec6-a0e9db1f091a')
      scriptOneTrust.id = 'onetrust-consent-script'
      oneTrustLocation.insertAdjacentElement('afterend', scriptOneTrust)
    }
  } catch (e) {
    logToSentry(IS_PRODUCTION ? LOG_CATEGORY.PROD_DEBUG : LOG_CATEGORY.STAGING_DEBUG, {
      userId: undefined,
      message: `Failed to load cookie banner`,
      type: 'error',
      e,
    })
  }
}

export const getParentDomain = () => {
  const hostname = window.location.hostname
  const urlParts = hostname.split('.')

  if (urlParts.length > 2) {
    return `.${urlParts.slice(1).join('.')}`
  }
  return hostname
}
